import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSubmissionHeatmapData } from '../slices/reportsSlice';
import { getHourMinuteInResourceTz, getResourceTimezoneLabel } from '../utils/datetime';
import { SUBMISSION_STATE } from '../utils/statusTokens';
import { dayState, monthGrid, summarizeDays } from '../utils/submissionCalendar';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// StatusHero.js re-fetches open-session counts every 60s (GATE_REFRESH_INTERVAL_MS,
// not exported); reuse the same cadence here so a session's open/missed
// state does not go stale between refetches of `sessions` itself.
const NOW_TICK_MS = 60 * 1000;

// Phone view of the submissions heatmap: a month calendar with a completion
// bar per day; tapping a day lists its sessions. Reads the data the heatmap
// already fetched (no request of its own).
export default function SubmissionCalendar({ year, month }) {
  const data = useSelector(selectSubmissionHeatmapData);
  const sessions = useMemo(() => (Array.isArray(data?.sessions) ? data.sessions : []), [data]);
  const [now, setNow] = useState(() => Date.now());

  // `sessions` only changes when the heatmap refetches (no polling on that
  // slice), so without an explicit tick, open/missed would freeze at
  // whatever `now` was on mount.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), NOW_TICK_MS);
    return () => clearInterval(tick);
  }, []);

  const byDay = useMemo(() => summarizeDays(sessions, now), [sessions, now]);
  const grid = monthGrid(year, month);
  const days = Object.keys(byDay).map(Number);
  const [selected, setSelected] = useState(null);
  const current = selected ?? (days.length ? Math.max(...days) : null);

  const selectDay = (day) => {
    setSelected(day);
    setNow(Date.now());
  };

  if (!sessions.length) {
    return <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No sessions this month yet.</p>;
  }

  const detail = current != null ? byDay[current] : null;
  const ordered = detail ? [...detail.sessions].sort((a, b) => new Date(a.gate_closure) - new Date(b.gate_closure)) : [];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-gray-500 dark:text-gray-400">
        {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((day, i) => {
          if (day == null) return <span key={`blank-${i}`} />;
          const summary = byDay[day];
          const state = dayState(summary);
          const on = day === current;
          const fill = summary ? Math.round((summary.submitted / summary.total) * 100) : 0;
          // The heatmap's palette: the submitted share fills green over the
          // gray track (missed stays gray); a day with sessions still open
          // fills amber instead. Days without sessions get no track.
          const tone = summary?.open > 0 ? SUBMISSION_STATE.pending.cell : SUBMISSION_STATE.ready.cell;
          const width = summary?.submitted > 0 || summary?.open > 0 ? Math.max(fill, 8) : 0;
          return (
            <button
              key={day}
              type="button"
              onClick={() => selectDay(day)}
              aria-pressed={on}
              aria-label={summary ? `${day}: ${summary.submitted} of ${summary.total} submitted` : `${day}: no sessions`}
              className={`flex h-12 flex-col items-center justify-center gap-1.5 rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                on ? 'border border-brand-400 bg-brand-50 font-semibold text-brand-700 dark:border-brand-600 dark:bg-brand-900/30 dark:text-brand-300' : 'text-gray-800 dark:text-gray-200'
              } ${state === 'none' ? 'text-gray-400 dark:text-gray-500' : ''}`}
            >
              {day}
              <span className={`flex h-1 w-5 overflow-hidden rounded-full ${state === 'none' ? '' : 'bg-gray-200 dark:bg-gray-700'}`}>
                <span className={`h-1 ${tone}`} style={{ width: `${width}%` }} />
              </span>
            </button>
          );
        })}
      </div>
      {detail && (
        <div className="rounded-lg border border-gray-200/70 p-3 dark:border-gray-600/70">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Day {current} · {detail.submitted} of {detail.total} submitted
            {detail.open > 0 && <span className="font-normal text-amber-700 dark:text-amber-400"> · {detail.open} open</span>}
            {detail.missed > 0 && <span className="font-normal text-gray-500 dark:text-gray-400"> · {detail.missed} missed</span>}
          </p>
          <div className="mt-2 grid gap-0.5" style={{ gridTemplateColumns: `repeat(${Math.max(ordered.length, 1)}, minmax(0, 1fr))` }}>
            {ordered.map((s) => (
              <span
                key={s.session_id}
                title={`${getHourMinuteInResourceTz(s.gate_closure)} ${getResourceTimezoneLabel(undefined, new Date(s.gate_closure))} · ${s.submitted ? 'submitted' : (s.open ? 'open' : 'missed')}`}
                className={`h-5 rounded-sm ${s.submitted ? SUBMISSION_STATE.ready.cell : (s.open ? SUBMISSION_STATE.pending.cell : SUBMISSION_STATE.missed.cell)}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
